import React, { useEffect, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { recentre } from '../state/gyroBridge';

// VRButton — turn the phone into a viewport.
//
// ── THE PERMISSION IS THE WHOLE PROBLEM ──────────────────────────────────────
// Since iOS 13, `deviceorientation` fires NOTHING until
// DeviceOrientationEvent.requestPermission() has resolved, and that call is only
// honoured from inside a real user gesture. Call it on mount, or from a promise
// chain, or after an await, and it rejects with no error you would notice — the
// events simply never arrive and the feature looks unimplemented.
//
// So this exists as a button, and the request is the FIRST thing in the click
// handler, before any await. That ordering is load-bearing, not style.
//
// It also needs HTTPS. The dev server already runs TLS (vite.config.js), which
// is why this is testable locally at all — on plain http the prompt never
// appears and, again, you get silence rather than an error.
const wrap = {
  position: 'fixed', right: 'max(16px, env(safe-area-inset-right))',
  bottom: 'max(150px, calc(env(safe-area-inset-bottom) + 150px))', zIndex: 75,
  display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end',
  pointerEvents: 'auto',
};
const btn = (on) => ({
  padding: '11px 18px', borderRadius: 999, cursor: 'pointer',
  border: `1px solid ${on ? 'rgba(157,255,125,0.6)' : 'rgba(255,178,94,0.5)'}`,
  background: on ? 'rgba(24,40,20,0.92)' : 'rgba(20,12,8,0.9)',
  color: on ? '#9dff7d' : '#ffe9c4', font: '700 14px system-ui',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
});
const note = { font: '600 11px system-ui', color: 'rgba(255,233,196,0.75)', maxWidth: 200, textAlign: 'right', lineHeight: 1.4 };

// Shown on touch devices, or forced with ?vr=1 so it can be exercised on a
// desktop — where the gyro will never fire, but the stereo split and the
// plumbing can still be checked.
const FORCED = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('vr') != null;

export default function VRButton() {
  const gyro = useWorldStore((s) => s.gyro);
  const stereo = useWorldStore((s) => s.stereo);
  const setGyro = useWorldStore((s) => s.setGyro);
  const setStereo = useWorldStore((s) => s.setStereo);
  const [msg, setMsg] = useState('');
  const [seen, setSeen] = useState(false);

  const touch = typeof window !== 'undefined'
    && (('ontouchstart' in window) || navigator.maxTouchPoints > 0);
  const show = FORCED || touch;

  // DID ANY EVENT ACTUALLY ARRIVE? Permission can be granted and the hardware
  // still report nothing (a desktop, a locked-down browser, a device with no
  // gyro). Without this the button says ON and the world does not move, which is
  // indistinguishable from the maths being wrong — and I would have gone looking
  // at the maths.
  useEffect(() => {
    if (!gyro) { setSeen(false); return undefined; }
    const probe = (e) => { if (e.alpha != null) setSeen(true); };
    window.addEventListener('deviceorientation', probe, true);
    const t = setTimeout(() => {
      setSeen((s) => {
        if (!s) setMsg('No motion data on this device — the plumbing is fine, the hardware is not reporting.');
        return s;
      });
    }, 2500);
    return () => { window.removeEventListener('deviceorientation', probe, true); clearTimeout(t); };
  }, [gyro]);

  if (!show) return null;

  const enable = (e) => {
    e.preventDefault();
    if (gyro) { setGyro(false); setStereo(false); setMsg(''); return; }
    const DOE = window.DeviceOrientationEvent;
    // FIRST STATEMENT IN THE HANDLER — see the note above. No await before it,
    // or iOS treats the call as unattached to the gesture and refuses.
    if (DOE && typeof DOE.requestPermission === 'function') {
      DOE.requestPermission()
        .then((state) => {
          if (state === 'granted') { recentre(); setGyro(true); setMsg(''); }
          else setMsg('Motion access denied. Safari → Settings → Motion & Orientation Access.');
        })
        .catch(() => setMsg('Motion access needs HTTPS and a direct tap.'));
      return;
    }
    // Android / desktop: no permission gate, events either fire or they do not.
    recentre();
    setGyro(true);
  };

  return (
    <div style={wrap}>
      {msg && <div style={note}>{msg}</div>}
      {gyro && (
        <>
          <div style={note}>
            {seen ? 'Turn your body to look · joystick to walk' : 'waiting for motion…'}
          </div>
          <button style={btn(stereo)} onClick={() => setStereo(!stereo)}>
            {stereo ? '◧ Split view ON' : '◧ Split view'}
          </button>
          <button style={btn(false)} onClick={() => { recentre(); setMsg('Re-centred — you are facing forward.'); }}>
            ⌖ Re-centre
          </button>
        </>
      )}
      <button style={btn(gyro)} onClick={enable}>{gyro ? '⛶ Exit VR' : '⛶ VR mode'}</button>
    </div>
  );
}
